import { createGtm, useGtm } from '@gtm-support/vue-gtm'
import { useRuntimeConfig } from '#app'
import { applyGtmUserProperties, resolveGtmUserProperties } from '~/composables/useGtmUserProperties'
import { resolveLayoutVersionNumber } from '~/composables/useLayoutVersion'
import { useCountryConfig } from '~/composables/useCountryConfig'

export default defineNuxtPlugin((nuxt) => {
  const id = useCountryConfig().gtmId
  const { gtmEnabled } = useRuntimeConfig().public

  if (!gtmEnabled || !id)
    return

  nuxt.vueApp.use(
    createGtm({
      id, // Your GTM single container ID, array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy'] or array of objects [{id: 'GTM-xxxxxx', queryParams: { gtm_auth: 'abc123', gtm_preview: 'env-4', gtm_cookies_win: 'x'}}, {id: 'GTM-yyyyyy', queryParams: {gtm_auth: 'abc234', gtm_preview: 'env-5', gtm_cookies_win: 'x'}}], // Your GTM single container ID or array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy']
      defer: false, // Script can be set to `defer` to speed up page load at the cost of less accurate results (in case visitor leaves before script is loaded, which is unlikely but possible). Defaults to false, so the script is loaded `async` by default
      compatibility: false, // Will add `async` and `defer` to the script tag to not block requests for old browsers that do not support `async`
      nonce: '2726c7f26c', // Will add `nonce` to the script tag
      enabled: true, // defaults to true. Plugin can be disabled by setting this to false for Ex: enabled: !!GDPR_Cookie (optional)
      debug: process.env.NODE_ENV !== 'production', // Whether or not display console logs debugs (optional)
      loadScript: true, // Whether or not to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
      trackOnNextTick: false, // Whether or not call trackView in Vue.nextTick
    }),
  )

  // Inject customer_type, search_engine_version and nav_version on every trackEvent.
  const gtm = useGtm()
  if (!gtm) return

  const userStore = useUserStore()
  const orderStore = useOrderHistoryStore()
  const { isNew } = useLayoutVersion()

  // Resolve user properties on mount.
  let userProperties = resolveGtmUserProperties({
    hasUserData: !!userStore.data,
    isHybrid: userStore.hasHybridSearchFeature,
    layoutVersion: resolveLayoutVersionNumber(isNew.value),
    hasAnyOrders: orderStore.hasAnyOrders,
    hasResolvedOrderHistory: orderStore.hasResolvedOrderHistory,
  })

  // Re-resolve user properties when user data, hybrid search feature, layout version, has any orders, or has resolved order history changes.
  watch(
    () => [
      userStore.data,
      userStore.hasHybridSearchFeature,
      isNew.value,
      orderStore.hasAnyOrders,
      orderStore.hasResolvedOrderHistory,
    ] as const,
    ([data]) => {
      userProperties = resolveGtmUserProperties({
        hasUserData: !!data,
        isHybrid: userStore.hasHybridSearchFeature,
        layoutVersion: resolveLayoutVersionNumber(isNew.value),
        hasAnyOrders: orderStore.hasAnyOrders,
        hasResolvedOrderHistory: orderStore.hasResolvedOrderHistory,
      })
    },
  )

  // Apply user properties to every trackEvent.
  const trackable = gtm as unknown as { trackEvent: (event?: Record<string, unknown>) => void }
  const nativeTrackEvent = trackable.trackEvent.bind(gtm)
  trackable.trackEvent = (event = {}) => {
    applyGtmUserProperties(event, userProperties)
    return nativeTrackEvent(event)
  }
})
